import { normalizeSkip, KEY_BY_STEP } from './plannerSkips';

/**
 * The plan document → what the planner screen shows. Eight beats (PROMPT SPACES VERIFY SOUND STAFF
 * PERMITS NUMBERS POSTER) read off `doc.stages`; a skipped stage counts as done. Pure functions, so
 * the stage, the rail, the cards and the log all agree on which beat we're on.
 */

export const ORDER = ['prompt', 'spaces', 'verify', 'sound', 'staff', 'permits', 'numbers', 'poster'];
export const STEP_LABELS = ['PROMPT', 'SPACES', 'VERIFY', 'SOUND', 'STAFF', 'PERMITS', 'NUMBERS', 'POSTER'];
export const STAGE_LABELS = ['READING YOUR PROMPT', 'FINDING A SPACE', 'VERIFYING THE ROOM', 'SETTING THE SOUND', 'BOOKING THE CREW', 'CHECKING PERMITS', 'RUNNING THE NUMBERS', 'ROOM · FULL'];
export const WORKING = new Set(['queued', 'running', 'revising']);
export const SETTLED = new Set(['ready', 'approved', 'booked']);

export const money = (cents) => `$${((cents || 0) / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const isDone = (stages, skip, key) => !!stages?.[key] || !!normalizeSkip(skip)[key];

/**
 * 1‥8. A settled plan is 8 (the room is full). Otherwise the first beat that hasn't closed —
 * never behind the stage the server says it is on.
 */
export function stepOf(plan) {
  if (!plan) return 1;
  if (SETTLED.has(plan.status)) return 8;
  const st = plan.plan?.stages || {};
  const skip = plan.plan?.skip;
  let step = ORDER.findIndex((k) => !isDone(st, skip, k));
  if (step < 0) step = 7;
  const server = ORDER.indexOf(plan.stage);
  if (server > step) step = server;
  return step + 1;
}

export const isSkippedStep = (skip, step) => !!normalizeSkip(skip)[KEY_BY_STEP[step]];

/** The header status: label + colour. */
export function statusOf(plan) {
  if (!plan) return { label: 'PLANNING', tone: 'pink', pulse: true };
  switch (plan.status) {
    case 'booked': return { label: 'BOOKED', tone: 'green' };
    case 'approved': return { label: 'APPROVED', tone: 'green' };
    case 'ready': return { label: 'PLAN READY', tone: 'green' };
    case 'failed': return { label: 'PLAN HELD', tone: 'held' };
    case 'budget_exhausted': return { label: 'BUDGET USED UP', tone: 'held' };
    case 'revising': return { label: 'RE-PLANNING', tone: 'pink', pulse: true };
    default: return { label: 'PLANNING', tone: 'pink', pulse: true };
  }
}

const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
export function whenChip(local) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local || '');
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const h = +m[4]; const min = m[5];
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${WEEKDAYS[d.getUTCDay()]} · ${hh}${min === '00' ? '' : `:${min}`} ${h < 12 ? 'AM' : 'PM'}`;
}

const ALCOHOL = { full_bar: 'OPEN BAR', beer_wine: 'BEER & WINE', cash_bar: 'CASH BAR' };

/** FROM YOUR PROMPT — up to six chips out of the intent. */
export function chipsOf(doc) {
  const it = doc?.intent || {};
  const out = [];
  if (it.title) out.push(it.title.toUpperCase());
  const when = whenChip(it.startLocal); if (when) out.push(when);
  const place = it.venueMode === 'own' && it.own?.name ? it.own.name : (doc?.regionName || it.city);
  if (place) out.push(String(place).split(',')[0].toUpperCase());
  if (it.headcount) out.push(`~${it.headcount} GUESTS`);
  if (it.wantsDj && it.music) out.push(String(it.music).toUpperCase());
  if (ALCOHOL[it.alcohol]) out.push(ALCOHOL[it.alcohol]);
  if (it.venueKindWanted) out.push(String(it.venueKindWanted).toUpperCase());
  if (it.food && it.food !== 'none') out.push(it.food.toUpperCase());
  return out.slice(0, 6);
}

const fmtDate = (ymd) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || ''); if (!m) return ''; const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])); return `${d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} ${d.getUTCDate()}`; };

/** NOW · {STEP} — the readout for a beat. `value: { countTo }` asks the HUD to count up. */
export function readoutOf(doc, step, plan) {
  const it = doc?.intent || {};
  const venues = doc?.venues || [];
  const chosen = venues.find((v) => v.id === doc?.chosenVenueId);
  const own = it.venueMode === 'own';
  const skip = normalizeSkip(doc?.skip);
  const staff = doc?.staff || [];
  const crew = staff.reduce((n, s) => n + (s.count || 0), 0);
  const cost = staff.reduce((n, s) => n + (s.costCents || 0), 0);
  const permits = doc?.permits || [];
  const required = permits.filter((p) => p.status === 'required');
  const soonest = required.map((p) => p.dueLocal).filter(Boolean).sort()[0];
  const fin = doc?.money || {};
  const details = chipsOf(doc).length || 0;
  switch (step) {
    case 1: return { key: 'BRIEF', value: details ? `${details} details` : 'Reading…', line: 'Pulling who, when, where and the money out of one line.' };
    case 2: return own
      ? { key: 'SPACES', value: 'Your space', line: `You named ${it.own?.name || 'your place'}. Checking it first.` }
      : { key: 'SPACES', value: doc?.venuesConsidered ? `${doc.venuesConsidered} rooms nearby` : 'Scouting…', line: `Looking near ${(doc?.regionName || it.city || 'you').split(',')[0]}.` };
    case 3: return chosen
      ? { key: 'VERIFY', value: chosen.name, line: chosen.basis === 'own' ? 'Yours. Nothing to verify.' : `${venues.length} verified for private hire${chosen.capacity ? ` · cap ${chosen.capacity}` : ''}.` }
      : { key: 'VERIFY', value: 'Checking the room', line: 'Who actually rents it, and for how much.' };
    case 4: return skip.sound
      ? { key: 'SOUND', value: 'Left out', line: 'No DJ or speakers, as asked.' }
      : it.wantsDj
        ? { key: 'SOUND', value: (doc?.sound || []).find((s) => s.businessId === doc?.chosenSoundId)?.name || 'DJ to be booked', line: `${(it.music || 'music').toUpperCase()} · ${it.durationHours || 0} hrs.` }
        : { key: 'SOUND', value: 'House system', line: 'No DJ needed for this one.' };
    case 5: return skip.staff
      ? { key: 'STAFF', value: 'Left out', line: 'You and your people run it.' }
      : crew > 0
        ? { key: 'STAFF', value: `${crew} crew`, line: `${doc?.staffHours || 0} hrs including setup. ${money(cost)}.` }
        : { key: 'STAFF', value: 'No crew', line: 'Small enough to run yourselves.' };
    case 6: return skip.permits
      ? { key: 'PERMITS', value: 'Left out', line: 'Nothing checked or filed, as asked.' }
      : required.length
        ? { key: 'PERMITS', value: `${required.length} to file`, line: soonest ? `Soonest due ${fmtDate(soonest)}.` : 'Nothing overdue yet.' }
        : { key: 'PERMITS', value: permits.length ? `${permits.length} of ${permits.length} done` : 'Nothing to file', line: 'Nothing left to file.' };
    case 7: return skip.numbers
      ? { key: 'NUMBERS', value: 'Free', line: 'Free to attend. No ticket, no break-even.' }
      : { key: 'NUMBERS', value: { countTo: fin.recommendedTicketCents || 0 }, line: `Recommended ticket. Break-even at ${fin.breakEvenGuests || 0} guests.` };
    default: return {
      key: 'READY', value: 'Room full.',
      line: plan?.posterUrl ? 'Poster drawn. Review it, then book.' : skip.poster ? 'No poster, as asked. Review it, then approve.' : 'Review it, then approve. The poster draws on approve.',
    };
  }
}

/** The log entries as the panel shows them: [{ t, text, kind }] with m:ss clocks from the first line. */
export function logEntries(plan) {
  const lines = plan?.plan?.log || [];
  const t0 = lines.length ? new Date(lines[0].at).getTime() : null;
  const clock = (at) => {
    const ms = t0 != null ? new Date(at).getTime() - t0 : 0;
    if (!Number.isFinite(ms) || ms < 0) return '0:00';
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  const entries = lines.map((l) => ({ at: l.at, t: clock(l.at), text: l.text, kind: 'log' }));
  const notes = (plan?.messages || []).filter((m) => m.role === 'user' && m.content !== plan?.prompt).map((m) => ({ at: m.createdAt, t: 'YOU', text: m.content, kind: 'note' }));
  return [...entries, ...notes].sort((a, b) => new Date(a.at) - new Date(b.at));
}
